import constants from '../constants';
import {IceCandidate} from 'models/IceCandidate';
import {JoinRoomResponse, OnJoinRoom} from 'models/WebRTCWebSocketsModels';
import {Platform} from 'react-native';
import {User} from 'models/User';

const INTERVAL_TIMEOUT = 4000;

export enum VideoType {
  screen = 'SCREEN',
  camera = 'CAMERA',
}

enum SendMessageType {
  chat = 'signal:chat',
  reaction = 'signal:reaction',
  muteAll = 'signal:mute_all',
  raiseHand = 'signal:raise_hand',
}

interface WebRTCWebSocketTypeModel {
  id: number;
  method: string;
}

const webRTCWebSocketTypeModel = {
  ping: {
    id: 0,
    method: 'ping',
  },
  leaveRoom: {
    id: 8,
    method: 'leaveRoom',
  },
  unpublishVideo: {
    id: 6,
    method: 'unpublishVideo',
  },
  sendMessage: {
    id: 9,
    method: 'sendMessage',
  },
  receiveVideoFrom: {
    id: 4,
    method: 'receiveVideoFrom',
  },
  publishVideo: {
    id: 2,
    method: 'publishVideo',
  },
  joinRoom: {
    id: 1,
    method: 'joinRoom',
  },
  onIceCandidate: {
    id: 5,
    method: 'onIceCandidate',
  },
  streamPropertyChanged: {
    id: 20,
    method: 'streamPropertyChanged',
  },
  unsubscribeFromVideo: {
    id: 7,
    method: 'unsubscribeFromVideo',
  },
  reconnectStream: {
    id: 18,
    method: 'reconnectStream',
  },
};

class WebRTCWebSockets {
  private webSocket: WebSocket | undefined;
  private interval: NodeJS.Timer | undefined;
  private userId: string;
  private user: User;
  private username: string;

  onOpen: (() => void) | undefined;
  onStreamPropertyChanged:
    | ((streamId: string, property: string, newValue: boolean) => void)
    | undefined;
  onIceCandidate:
    | ((iceCandidate: IceCandidate, senderConnectionId: string) => void)
    | undefined;
  onParticipantJoined:
    | ((id: string, username: string, type: VideoType) => void)
    | undefined;
  onParticipantPublished:
    | ((connectionId: string, streamId: string, type: VideoType) => void)
    | undefined;
  onParticipantDisconnected: ((connectionId: string) => void) | undefined;
  onJoinRoom: ((models: OnJoinRoom[]) => void) | undefined;
  onSdpAnswerReceived: ((sdpAnswer: string) => void) | undefined;
  onPublishVideo: ((streamId: string) => void) | undefined;
  onMessageReceived:
    | ((fromConnectionId: string, data: string) => void)
    | undefined;
  onReactionReceived:
    | ((fromConnectionId: string, data: string) => void)
    | undefined;
  onRaiseHandReceived:
    | ((fromConnectionId: string, data: string) => void)
    | undefined;
  onMuteAll: ((fromConnectionId: string) => void) | undefined;

  constructor(sessionId: string, userId: string, user: User, username: string) {
    this.userId = userId;
    this.user = user;
    this.username = username;
    this.configure(sessionId);
  }

  private configure = (sessionId: string) => {
    this.webSocket = new WebSocket(
      `wss://${constants.OPENVIDU_SERVER}/openvidu?sessionId=${sessionId}`,
    );

    this.webSocket.onopen = async () => {
      console.log('onopen');
      this.interval = setInterval(() => {
        this.send(webRTCWebSocketTypeModel.ping, {
          params: {
            interval: INTERVAL_TIMEOUT,
          },
        });
      }, INTERVAL_TIMEOUT);

      this.onOpen && this.onOpen();
    };

    this.webSocket.onerror = e => {
      console.log('onerror', e);
    };

    this.webSocket.onclose = e => {
      console.log('onclose', e);
    };

    this.webSocket.onmessage = e => {
      const messageData = JSON.parse(e.data);

      if (messageData.id == 2) {
        messageData.result.id &&
          this.onPublishVideo &&
          this.onPublishVideo(messageData.result.id);
      }

      if (messageData.method == 'streamPropertyChanged') {
        this.onStreamPropertyChanged &&
          this.onStreamPropertyChanged(
            messageData.params.streamId,
            messageData.params.property,
            messageData.params.newValue === 'true',
          );
      }

      if (messageData.result?.sdpAnswer) {
        this.onSdpAnswerReceived &&
          this.onSdpAnswerReceived(messageData.result?.sdpAnswer);
      }

      if (messageData.method == 'iceCandidate') {
        const iceCandidate: IceCandidate = {
          candidate: messageData.params.candidate,
          sdpMLineIndex: messageData.params.sdpMLineIndex,
          sdpMid: messageData.params.sdpMid,
        };

        this.onIceCandidate &&
          this.onIceCandidate(
            iceCandidate,
            messageData.params.senderConnectionId,
          );
      }

      if (messageData.method == 'sendMessage') {
        if (messageData.params.type.includes('mute_all')) {
          this.onMuteAll && this.onMuteAll(messageData.params.from);
        }

        if (messageData.params.type.includes('raise_hand')) {
          this.onRaiseHandReceived &&
            this.onRaiseHandReceived(
              messageData.params.from,
              messageData.params.data,
            );
        }

        if (messageData.params.type.includes('reaction')) {
          this.onReactionReceived &&
            this.onReactionReceived(
              messageData.params.from,
              messageData.params.data,
            );
        }

        if (messageData.params.type.includes('chat')) {
          this.onMessageReceived &&
            this.onMessageReceived(
              messageData.params.from,
              messageData.params.data,
            );
        }
      }

      if (messageData.method == 'participantJoined') {
        const metadata = JSON.parse(messageData.params.metadata);

        this.onParticipantJoined &&
          this.onParticipantJoined(
            messageData.params.id,
            metadata.clientData,
            metadata.type,
          );
      }

      if (
        messageData.method == 'participantLeft' ||
        messageData.method == 'participantEvicted'
      ) {
        this.onParticipantDisconnected &&
          this.onParticipantDisconnected(messageData.params.connectionId);
      }

      if (messageData.method == 'participantPublished') {
        if (messageData.params?.streams?.length > 0) {
          const newParticipantStream = messageData.params?.streams[0];
          const id = messageData.params.id;

          this.onParticipantPublished &&
            this.onParticipantPublished(
              id,
              newParticipantStream.id,
              newParticipantStream?.typeOfVideo,
            );
        }
      }

      if (
        Array.isArray(messageData.result?.value) &&
        messageData.result?.value?.length > 0
      ) {
        const model = messageData as JoinRoomResponse;
        const resultWithUndefined = model.result?.value?.map(item => {
          const metadata = JSON.parse(item.metadata);
          if (item.streams?.length > 0) {
            return {
              streamId: item.streams[0].id,
              id: item.id,
              username: metadata.clientData,
              type: metadata.type,
              audioActive: item.streams[0].audioActive,
              videoActive: item.streams[0].videoActive,
            };
          }

          return undefined;
        });

        const result = resultWithUndefined.filter(
          item => item != undefined,
        ) as OnJoinRoom[];

        this.onJoinRoom && this.onJoinRoom(result);
      }
    };
  };

  sendStreamPropertyChanged = (
    streamId: string,
    newValue: boolean,
    isVideo: boolean,
  ) => {
    this.send(webRTCWebSocketTypeModel.streamPropertyChanged, {
      params: {
        streamId,
        property: isVideo ? 'videoActive' : 'audioActive',
        newValue,
        reason: isVideo ? 'publishVideo' : 'publishAudio',
      },
    });
  };

  sendIceCandidate = (connectionId: string, iceCandidate: IceCandidate) => {
    this.send(webRTCWebSocketTypeModel.onIceCandidate, {
      params: {
        endpointName: connectionId,
        candidate: iceCandidate.candidate,
        sdpMid: (iceCandidate.sdpMid == 'video' ? 1 : 0).toString(),
        sdpMLineIndex: iceCandidate.sdpMLineIndex,
      },
    });
  };

  sendJoinRoom = (token: string, sessionId: string, type: VideoType) => {
    const metadata = {
      clientData: this.username,
      name: this.user.name,
      lastname: this.user.surname,
      email: this.user.email,
      participantId: this.userId,
      type: type,
    };

    this.send(webRTCWebSocketTypeModel.joinRoom, {
      params: {
        token: token,
        session: sessionId,
        platform: Platform.OS,
        sdkVersion: '2.23.0',
        metadata: JSON.stringify(metadata),
        secret: '',
        recorder: false,
      },
    });
  };

  sendPublishVideo = (sdpOffer: string, type: VideoType) => {
    this.send(webRTCWebSocketTypeModel.publishVideo, {
      params: {
        doLoopback: false,
        hasAudio: type === VideoType.camera,
        hasVideo: true,
        audioActive: type === VideoType.camera,
        videoActive: true,
        typeOfVideo: type,
        frameRate: -1,
        videoDimensions: {
          width: 640,
          height: 480,
        },
        sdpOffer: sdpOffer,
      },
    });
  };

  sendReceiveVideoFrom = (sdpOffer: string, streamId: string) => {
    this.send(webRTCWebSocketTypeModel.receiveVideoFrom, {
      params: {
        sdpOffer: sdpOffer,
        sender: streamId,
      },
    });
  };

  sendUnsubscribeFromVideo = (connectionId: string) => {
    this.send(webRTCWebSocketTypeModel.unsubscribeFromVideo, {
      params: {
        sender: connectionId,
      },
    });
  };

  sendReconnectStream = (sdpOffer: string, streamId: string) => {
    this.send(webRTCWebSocketTypeModel.reconnectStream, {
      params: {
        sdpOffer: sdpOffer,
        stream: streamId,
      },
    });
  };

  sendSendMessage = (message: string) => {
    const messageObject = {
      to: [],
      data: JSON.stringify({
        message: message,
        nickname: this.username,
      }),
      type: SendMessageType.chat,
    };

    this.send(webRTCWebSocketTypeModel.sendMessage, {
      params: {
        message: JSON.stringify(messageObject),
      },
    });
  };

  sendReaction = (reaction: string) => {
    const messageObject = {
      to: [],
      data: JSON.stringify({
        reaction: reaction,
        nickname: this.username,
      }),
      type: SendMessageType.reaction,
    };

    this.send(webRTCWebSocketTypeModel.sendMessage, {
      params: {
        message: JSON.stringify(messageObject),
      },
    });
  };

  sendMuteAll = () => {
    const messageObject = {
      to: [],
      data: JSON.stringify({
        nickname: this.username,
      }),
      type: SendMessageType.muteAll,
    };

    this.send(webRTCWebSocketTypeModel.sendMessage, {
      params: {
        message: JSON.stringify(messageObject),
      },
    });
  };

  sendRaiseHand = () => {
    const messageObject = {
      to: [],
      data: JSON.stringify({
        nickname: this.username,
      }),
      type: SendMessageType.raiseHand,
    };

    this.send(webRTCWebSocketTypeModel.sendMessage, {
      params: {
        message: JSON.stringify(messageObject),
      },
    });
  };

  sendUnpublishVideo = () => {
    this.send(webRTCWebSocketTypeModel.unpublishVideo, {
      params: {},
    });
  };

  sendLeaveRoom = () => {
    this.send(webRTCWebSocketTypeModel.leaveRoom, {
      params: {},
    });
  };

  disconnect = () => {
    clearInterval(this.interval);
    this.interval = undefined;

    this.sendUnpublishVideo();
    this.sendLeaveRoom();

    this.webSocket?.close();
  };

  private send = (type: WebRTCWebSocketTypeModel, model: object) => {
    this.webSocket?.send(
      JSON.stringify({
        ...model,
        ...type,
        jsonrpc: '2.0',
      }),
    );
  };
}

export default WebRTCWebSockets;
